import { Platform } from 'react-native';
import { Alert } from '../components/rn';
import { api, ApiError, type PasskeyConfirmationRequest } from '../services/api';
import { createPasskey, getPasskey, passkeysAvailable } from './authenticator';
import type { PasskeyFailure, PasskeyOutcome } from './types';

/**
 * 9.33.9.3 / 9.33.9.4 — adding a passkey, confirming with one, and the confirmation a
 * message to the task chat carries (D-780).
 *
 * The server decides what needs a confirmation and checks every answer; this module
 * only gathers the answer. Nothing here is trusted on its own.
 */

/** The words of the one extra tap the web may need (see `withTap`). */
export type TapPrompt = { title: string; action: string; cancel: string };

/**
 * Safari starts a passkey only from a tap, and an options request in between can use
 * the tap up. When the first try fails that way, the same options are offered once
 * more behind a button. A second refusal counts as a cancel.
 */
export async function withTap(run: () => Promise<PasskeyOutcome>, prompt: TapPrompt): Promise<PasskeyOutcome> {
  const first = await run();
  if (first.ok || first.reason !== 'needsGesture') return first;
  return new Promise((resolve) => {
    const settle = (outcome: PasskeyOutcome) => resolve(
      !outcome.ok && outcome.reason === 'needsGesture' ? { ok: false, reason: 'cancelled' } : outcome);
    Alert.alert(prompt.title, undefined, [
      { text: prompt.cancel, style: 'cancel', onPress: () => resolve({ ok: false, reason: 'cancelled' }) },
      // Started inside the button's own press, so the browser sees the tap.
      { text: prompt.action, onPress: () => { run().then(settle); } },
    ], { cancelable: true, onDismiss: () => resolve({ ok: false, reason: 'cancelled' }) });
  });
}

export type ConfirmResult =
  | { ok: true; confirmation: object }
  | { ok: false; reason: PasskeyFailure | 'noPasskey' };

/** Asks the server for options bound to `request`, then asks the passkey to sign them. */
export async function confirmWithPasskey(
  token: string, request: PasskeyConfirmationRequest, prompt: TapPrompt,
): Promise<ConfirmResult> {
  let options: object;
  try {
    options = await api.passkeyConfirmationOptions(token, request);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404 && error.code === 'no_passkey') {
      return { ok: false, reason: 'noPasskey' };
    }
    throw error;
  }
  if (!passkeysAvailable()) return { ok: false, reason: 'unsupported' };
  const outcome = await withTap(() => getPasskey(options), prompt);
  return outcome.ok ? { ok: true, confirmation: outcome.value } : outcome;
}

/** A plain name for the device, shown in the list. Brand names need no translation. */
export function deviceLabel(
  os: string = Platform.OS, isPad = (Platform as { isPad?: boolean }).isPad === true,
  userAgent: string = typeof navigator === 'undefined' ? '' : String(navigator.userAgent ?? ''),
): string | null {
  if (os === 'ios') return isPad ? 'iPad' : 'iPhone';
  if (os === 'android') return 'Android';
  if (/Edg\//.test(userAgent)) return 'Edge';
  if (/Firefox\//.test(userAgent)) return 'Firefox';
  if (/Chrome\//.test(userAgent)) return 'Chrome';
  if (/Safari\//.test(userAgent)) return 'Safari';
  return null;
}

export type AddResult = 'added' | 'reauth' | PasskeyFailure | 'noPasskey';

/**
 * Adds a passkey. The first one needs a recent sign-in or a confirmed number
 * (`reauth`, from `/passkeys/reauth`); a further one needs an existing passkey.
 * Answers 'reauth' when the server wants the number confirmed first.
 */
export async function addPasskey(
  token: string, reauth: string | null, prompt: TapPrompt, existing: number,
): Promise<AddResult> {
  if (!passkeysAvailable()) return 'unsupported';
  let proof: { confirmation?: object; reauth?: string } = reauth ? { reauth } : {};
  if (existing > 0) {
    const confirmed = await confirmWithPasskey(token, { purpose: 'add' }, prompt);
    if (!confirmed.ok) return confirmed.reason;
    proof = { confirmation: confirmed.confirmation };
  }
  let options: object;
  try {
    options = await api.passkeyRegistrationOptions(token, proof);
  } catch (error) {
    if (error instanceof ApiError && error.status === 403 && error.code === 'reauth_required') return 'reauth';
    throw error;
  }
  const created = await withTap(() => createPasskey(options), prompt);
  if (!created.ok) return created.reason;
  await api.registerPasskey(token, created.value, deviceLabel() ?? 'Passkey');
  return 'added';
}

/**
 * D-876 — after the 48-hour wait, the passkey that replaces the lost ones. The server
 * binds this registration to the recovery and retires the old passkeys in the same step.
 */
export async function recoverPasskey(token: string, reauth: string, prompt: TapPrompt): Promise<AddResult> {
  if (!passkeysAvailable()) return 'unsupported';
  let options: object;
  try {
    options = await api.passkeyRegistrationOptions(token, { recovery: true, reauth });
  } catch (error) {
    if (error instanceof ApiError && error.status === 403 && error.code === 'reauth_required') return 'reauth';
    throw error;
  }
  const created = await withTap(() => createPasskey(options), prompt);
  if (!created.ok) return created.reason;
  await api.registerPasskey(token, created.value, deviceLabel() ?? 'Passkey', true);
  return 'added';
}

/** D-876 — whoever still holds a passkey stops a reset with it. */
export async function cancelPasskeyRecovery(token: string, prompt: TapPrompt): Promise<'stopped' | PasskeyFailure | 'noPasskey'> {
  const confirmed = await confirmWithPasskey(token, { purpose: 'recovery_cancel' }, prompt);
  if (!confirmed.ok) return confirmed.reason;
  await api.cancelPasskeyRecovery(token, confirmed.confirmation);
  return 'stopped';
}

export type SignInResult =
  | { ok: true; token: string; onboarded: boolean }
  | { ok: false; reason: 'cancelled' | 'none' | 'unsupported' | 'unavailable' | 'failed' };

/**
 * D-882 — sign in with a passkey: Face ID, Touch ID, a fingerprint or the screen lock,
 * whichever the device uses. No number is typed; the passkey names the member.
 */
export async function signInWithPasskey(prompt: TapPrompt): Promise<SignInResult> {
  if (!passkeysAvailable()) return { ok: false, reason: 'unsupported' };
  let options: object;
  try {
    options = await api.passkeySignInOptions();
  } catch (error) {
    if (error instanceof ApiError && error.code === 'passkey_sign_in_unavailable') return { ok: false, reason: 'unavailable' };
    throw error;
  }
  const outcome = await withTap(() => getPasskey(options), prompt);
  if (!outcome.ok) {
    // On a device with no passkey for nosubject, iOS and Android answer "no credential".
    return { ok: false, reason: outcome.reason === 'cancelled' || outcome.reason === 'needsGesture' ? 'cancelled'
      : outcome.reason === 'unsupported' ? 'unsupported' : 'none' };
  }
  try {
    const session = await api.passkeySignIn(outcome.value);
    return { ok: true, token: session.token, onboarded: session.onboarded };
  } catch (error) {
    if (error instanceof ApiError && error.code === 'unknown_passkey') return { ok: false, reason: 'none' };
    if (error instanceof ApiError && error.status === 400) return { ok: false, reason: 'failed' };
    throw error;
  }
}

/** Removing one is confirmed with a passkey, so an SMS code alone cannot switch the rule off. */
export async function removePasskey(token: string, id: string, prompt: TapPrompt): Promise<'removed' | PasskeyFailure | 'noPasskey'> {
  const confirmed = await confirmWithPasskey(token, { purpose: 'revoke', passkey_id: id }, prompt);
  if (!confirmed.ok) return confirmed.reason;
  await api.revokePasskey(token, id, confirmed.confirmation);
  return 'removed';
}

// ---------------------------------------------------------------------------
// 9.33.9.4 — the task chat.

/** Which chat needs a confirmation, per session. A failed lookup is not cached. */
const taskChannels = new Map<string, Promise<number | null>>();

export function taskChannelReceiver(token: string): Promise<number | null> {
  const known = taskChannels.get(token);
  if (known) return known;
  const lookup = api.listPasskeys(token)
    .then((list) => (typeof list.taskChannelReceiverId === 'number' ? list.taskChannelReceiverId : null))
    .catch(() => {
      taskChannels.delete(token);
      return null;
    });
  taskChannels.set(token, lookup);
  return lookup;
}

/** For tests, and after a sign-out. */
export function forgetTaskChannels(): void {
  taskChannels.clear();
}

/** Why a message to the task chat was not sent. The draft stays. */
export type PasskeyStopReason = 'cancelled' | 'needsPasskey' | 'unsupported' | 'failed';

export class PasskeyStop extends Error {
  constructor(readonly reason: PasskeyStopReason) {
    super(`passkey_${reason}`);
    this.name = 'PasskeyStop';
  }
}

type Confirmation = { kind: 'none' } | { kind: 'confirmed'; confirmation: object };

async function confirmTaskMessage(
  token: string, receiverId: number, type: string, content: string, prompt: TapPrompt,
): Promise<Confirmation> {
  let result: ConfirmResult;
  try {
    result = await confirmWithPasskey(token, { purpose: 'task_message', receiver_id: receiverId, type, content }, prompt);
  } catch (error) {
    // The server no longer counts this chat as the task chat: nothing to confirm.
    if (error instanceof ApiError && error.status === 404 && error.code === 'not_task_channel') {
      taskChannels.delete(token);
      return { kind: 'none' };
    }
    throw error;
  }
  if (result.ok) return { kind: 'confirmed', confirmation: result.confirmation };
  // D-780 "Mandatory in the app": with no passkey yet, the sender adds one first.
  // An app that cannot hold one sends as before; the server enforces from the
  // sender's first passkey, and switches the rule on for all three later.
  if (result.reason === 'noPasskey') {
    if (passkeysAvailable()) throw new PasskeyStop('needsPasskey');
    return { kind: 'none' };
  }
  if (result.reason === 'unsupported') throw new PasskeyStop('unsupported');
  if (result.reason === 'cancelled' || result.reason === 'needsGesture') throw new PasskeyStop('cancelled');
  throw new PasskeyStop('failed');
}

export type TaskMessage = {
  token: string;
  receiverId: number;
  type: string;
  content: string;
  /** The task chat is always with an official account; other chats skip the lookup. */
  official: boolean;
  prompt: TapPrompt;
};

/**
 * Sends one message. On the task chat it first gathers a confirmation bound to this
 * exact receiver, type and content. If the server refuses an unconfirmed message
 * because this app did not know the chat (an older list, a failed lookup), the
 * confirmation is gathered and the message sent once more.
 *
 * Throws `PasskeyStop` when the message was not sent for a passkey reason.
 */
export async function sendWithTaskConfirmation<T>(
  message: TaskMessage, send: (confirmation?: object) => Promise<T>,
): Promise<T> {
  const { token, receiverId, type, content, official, prompt } = message;
  const first: Confirmation = official && (await taskChannelReceiver(token)) === receiverId
    ? await confirmTaskMessage(token, receiverId, type, content, prompt)
    : { kind: 'none' };
  try {
    return await send(first.kind === 'confirmed' ? first.confirmation : undefined);
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 403) throw error;
    if (error.code === 'passkey_required' && first.kind === 'none') {
      taskChannels.delete(token);
      const second = await confirmTaskMessage(token, receiverId, type, content, prompt);
      if (second.kind === 'confirmed') return send(second.confirmation);
      throw new PasskeyStop('failed');
    }
    if (error.code === 'passkey_required' || error.code === 'passkey_invalid') throw new PasskeyStop('failed');
    throw error;
  }
}
